import { ALL_ZONES, POS_ICON, DAYS, RESUME_GRACE_DAYS } from './constants';

export const uid = () => crypto.randomUUID().replace(/-/g, '');
export const haptic = () => { try { navigator.vibrate?.(45); } catch {} };
export const sanitize = (s, max = 500) => typeof s === 'string' ? s.trim().slice(0, max) : '';

export const toLocal = iso => {
  const d = new Date(iso), p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const fromLocal = s => new Date(s).toISOString();
export const fmtDate = iso => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
export const fmtTime = iso => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const daysAgo = iso => {
  const now = new Date(), then = new Date(iso);
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const d = Math.round((nowDay - thenDay) / 86400000);
  return d === 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d}d ago`;
};

export const budDays = bud => {
  if (!bud) return null;
  const now = new Date();
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const then = new Date(bud);
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  return Math.floor((thenDay - nowDay) / 86400000);
};
export const isBudExpired = bud => bud && budDays(bud) < 0;
export const budColor = (bud, dm) => {
  if (!bud) return dm ? '#3a5060' : '#7799aa';
  const d = budDays(bud);
  return d < 0 ? '#e84040' : d < 10 ? '#e84040' : d < 30 ? '#e87030' : '#30c890';
};

export const calcDose = (vol, conc) => conc > 0 ? `${parseFloat((parseFloat(vol || 0) * conc).toFixed(1))}mg` : null;
export const fmtVol = vol => parseFloat((parseFloat(vol || 0)).toFixed(3));
export const calcRemaining = (vial, allShots) => {
  // Dose-based vials (pen / autoinjector / pill): each shot consumes its own stamped
  // pillCount (1 for pen/autoinjector, and for any shot logged before this field
  // existed — see CLAUDE.md's "Pills per Dose" section for why this is stamped per
  // shot rather than read live from the vial's current pillsPerDose).
  if (vial.totalDoses) {
    const total = parseInt(vial.totalDoses) || 0;
    if (vial.status !== 'open') return total;
    const used = (vial.openedAt
      ? allShots.filter(s => s.compound === vial.compound && new Date(s.date) >= new Date(vial.openedAt))
      : allShots.filter(s => s.compound === vial.compound)
    ).reduce((sum, s) => sum + (s.pillCount || 1), 0);
    return Math.max(0, total - used);
  }
  if (!vial.totalVol) return 0;
  // splitOffset: mL drawn from the previous vial during a split — not counted against this vial
  const splitOffset = vial.splitOffset || 0;
  if (!vial.openedAt) {
    if (vial.status !== 'open') return parseFloat(vial.totalVol);
    const used = allShots
      .filter(s => s.compound === vial.compound)
      .reduce((sum, s) => sum + parseFloat(s.vol || 0), 0);
    return Math.max(0, parseFloat(vial.totalVol) - used + splitOffset);
  }
  const used = allShots
    .filter(s => s.compound === vial.compound && new Date(s.date) >= new Date(vial.openedAt))
    .reduce((sum, s) => sum + parseFloat(s.vol || 0), 0);
  return Math.max(0, parseFloat(vial.totalVol) - used + splitOffset);
};

export function calcTotalRemaining(vials, shots, isPen, compoundKey) {
  const datedOpen = vials.filter(v => v.openedAt && v.status === 'open');
  const earliestOpenMs = datedOpen.length > 0
    ? Math.min(...datedOpen.map(v => new Date(v.openedAt).getTime()))
    : Infinity;
  return vials.reduce((sum, v) => {
    const cap = isPen ? (parseInt(v.totalDoses) || 0) : (parseFloat(v.totalVol) || 0);
    if (v.status === 'new') return sum + cap;
    if (v.openedAt || earliestOpenMs === Infinity) return sum + Math.max(0, calcRemaining(v, shots) || 0);
    const usedBefore = isPen
      ? shots.filter(s => s.compound === compoundKey && new Date(s.date).getTime() < earliestOpenMs).length
      : shots.filter(s => s.compound === compoundKey && new Date(s.date).getTime() < earliestOpenMs)
          .reduce((a, x) => a + parseFloat(x.vol || 0), 0);
    return sum + Math.max(0, cap - usedBefore + (v.splitOffset || 0));
  }, 0);
}

export const weekShots = (shots, k) => {
  const s = new Date(); s.setDate(s.getDate() - s.getDay()); s.setHours(0, 0, 0, 0);
  return shots.filter(x => (!k || x.compound === k) && new Date(x.date) >= s).length;
};
export const lastFor = (shots, k) => [...shots].filter(s => s.compound === k).sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;
export const byMonth = shots => {
  const g = {};
  [...shots].sort((a, b) => new Date(b.date) - new Date(a.date)).forEach(s => {
    const k = new Date(s.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!g[k]) g[k] = [];
    g[k].push(s);
  });
  return g;
};

export const buildFullShots = (userShots = []) => {
  const all = [...userShots];
  const seen = new Set();
  return all.filter(s => { if (seen.has(s.id)) return false; seen.add(s.id); return true; })
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};

export const cmToImperial = cm => {
  if (!cm || cm === '') return '';
  const n = parseFloat(cm); if (isNaN(n)) return '';
  const ft = Math.floor(n / 30.48), inch = Math.round((n % 30.48) / 2.54);
  return `${ft}'${inch}"`;
};
export const imperialToCm = str => {
  if (!str || str === '') return '';
  const match = str.match(/(\d+)'(\d+)?/); if (!match) return '';
  return String(Math.round((parseInt(match[1]) || 0) * 30.48 + (parseInt(match[2]) || 0) * 2.54));
};
export const kgToLbs = kg => Math.round(parseFloat(kg) * 2.20462 * 10) / 10;
export const lbsToKg = lbs => Math.round(parseFloat(lbs) / 2.20462 * 10) / 10;
export const convertWeight = (value, fromUnit, toUnit) => {
  const v = parseFloat(value);
  if (!v || fromUnit === toUnit) return v;
  return fromUnit === 'lbs' ? lbsToKg(v) : kgToLbs(v);
};

export const convertBloodGlucose = (value, fromUnit, toUnit) => {
  const v = parseFloat(value);
  if (!v || fromUnit === toUnit) return v;
  // mg/dL is conventionally shown as a whole number; mmol/L to one decimal place —
  // matching how lbsToKg/kgToLbs round to sensible precision for their own units above,
  // rather than leaving raw floating-point noise.
  return toUnit === 'mg/dL' ? Math.round(v * 18.0182) : Math.round((v / 18.0182) * 10) / 10;
};

export const normSiteLocal = s => { if (!s) return 'lower_abd:L'; if (s.includes(':')) return s; return s + ':L'; };
export const displaySite = s => {
  const [z, p] = normSiteLocal(s).split(':');
  const zone = ALL_ZONES.find(x => x.id === z);
  return `${zone?.label || z} ${POS_ICON[p] || p}`;
};

export const isCountedDelivery = dt => dt === 'pen' || dt === 'autoinjector' || dt === 'pill';
export const isOralDelivery = dt => dt === 'pill';

// A shot with no `mgPerPill` key at all predates this feature and must always be treated
// as mg-tracked (the only interpretation that existed before) — never miscategorized as
// count-only just because the field happens to be absent. Only a shot that explicitly has
// the key, set to null (stamped that way by buildAndCommitShot/handleLogBatch for a bottle
// with no per-pill mg value), is genuinely count-only.
export const isCountOnlyPillShot = shot => !!shot && 'mgPerPill' in shot && shot.mgPerPill == null;

export const localDateStr = (d = new Date()) => {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Formats an already-local 'YYYY-MM-DD' string as MM/DD/YY via plain string splitting --
// no Date parsing at all, so there's no risk of the UTC-midnight-vs-local-day shift that
// `new Date('YYYY-MM-DD')` can introduce near midnight in negative-UTC-offset timezones.
export const fmtYMD = dayStr => {
  const [y, m, d] = dayStr.split('-');
  return `${m}/${d}/${y.slice(-2)}`;
};

export const freqChanged = (oldFreq, newFreq) => {
  const a = new Set(oldFreq || []);
  const b = new Set(newFreq || []);
  if (a.size !== b.size) return true;
  for (const day of a) if (!b.has(day)) return true;
  return false;
};

// Resolves the break entry (if any) covering a given local day (YYYY-MM-DD). Undefined
// cycleBreaks (every compound before this feature existed) always returns undefined, so
// this is purely additive -- no migration needed.
export const isOnBreak = (c, dayStr = localDateStr()) =>
  (c.cycleBreaks || []).find(b => b.from <= dayStr && (b.to == null || dayStr <= b.to));

// Whether a compound currently has an open (unresumed) break, regardless of what day it
// is -- distinct from isOnBreak, which answers "was THIS DAY covered by a break" and stays
// true through the rest of the day a break is resumed (by design, for freqForDay/calcStreak).
// UI that needs to flip the instant Resume is tapped must use this, not isOnBreak(c).
export const currentBreak = c => (c.cycleBreaks || []).find(b => b.to == null);

// Resolves which freq was actually in effect for a compound on a given local day
// (YYYY-MM-DD). Shared by calcStreak and TrendsView's Adherence chart so both judge
// past days against the schedule that was really active then, not whatever freq is
// stored now.
//
// Prefers freqHistory (an array of { freq, from } segments, `from` a local YYYY-MM-DD
// string or null for "since before we started recording" -- sorted ascending, kept in
// sync with the compound's current `freq` on every edit that changes it). Segments
// accumulate indefinitely, so a schedule from 3 edits ago is never silently lost the
// way a single prevFreq slot would be by a later edit overwriting it.
//
// Falls back to the older single-level freqUpdatedAt/prevFreq pair for any compound
// that predates freqHistory and hasn't been edited since -- this is what keeps the
// change migration-free; a compound only ever gains freqHistory the first time it's
// edited under this scheme (see nextFreqHistory below).
export const freqForDay = (c, dayStr) => {
  // A day inside an on-break window is never scheduled, regardless of what freqHistory
  // says -- checked first so calcStreak and TrendsView's Adherence chart (both already
  // routed through this helper) become break-aware with no changes of their own.
  if (isOnBreak(c, dayStr)) return [];
  if (Array.isArray(c.freqHistory) && c.freqHistory.length > 0) {
    // No pre-loop default to freqHistory[0].freq: a brand-new compound's only segment
    // has `from` = its creation date, so a day before that date must resolve to "not
    // scheduled" ([]), not silently inherit that segment's freq -- otherwise adding a
    // new compound retroactively makes calcStreak think it should have had shots logged
    // on past days before it even existed, breaking an otherwise-unbroken streak.
    let applicable = null;
    for (const entry of c.freqHistory) {
      if (entry.from == null || entry.from <= dayStr) applicable = entry.freq;
      else break;
    }
    return applicable ?? [];
  }
  return (!c.freqUpdatedAt || dayStr >= c.freqUpdatedAt) ? c.freq : (c.prevFreq || []);
};

// Builds the freqHistory to persist when a compound's freq is edited to newFreq.
// Only called once freqChanged(existing.freq, newFreq) is true -- an unchanged save
// should leave history untouched, which the caller (saveCmpd) already handles by not
// calling this at all in that case.
//
// If `existing` already has freqHistory, appends to it (preserving everything).
// Otherwise migrates whatever the older freqUpdatedAt/prevFreq fields (or neither)
// already recorded into the first history segment(s), so a compound's prior history
// is captured exactly once, on its first edit under this scheme, rather than being
// silently discarded the way a plain prevFreq overwrite would.
export const nextFreqHistory = (existing, newFreq) => {
  const base = Array.isArray(existing.freqHistory) && existing.freqHistory.length > 0
    ? existing.freqHistory
    : existing.freqUpdatedAt
      ? [{ freq: existing.prevFreq || [], from: null }, { freq: existing.freq || [], from: existing.freqUpdatedAt }]
      : [{ freq: existing.freq || [], from: null }];
  return [...base, { freq: newFreq, from: localDateStr() }];
};

// After a Cycle Break closes, a compound isn't held to the reactivated schedule for streak
// purposes (freqForDay itself is untouched -- adherence reporting should still show the
// literal miss) until the user logs a dose again, bounded to RESUME_GRACE_DAYS after the
// break ends. See RESUME_GRACE_DAYS in lib/constants.jsx for why. `key` is the compound's
// key in `compounds`/`shots`, needed since a compound alone doesn't carry its own shots.
export function resumeGraceActive(c, key, shots, dayStr) {
  const closedBreaks = (c.cycleBreaks || []).filter(b => b.to != null);
  if (!closedBreaks.length) return false;
  const mostRecentTo = closedBreaks.reduce((max, b) => (b.to > max ? b.to : max), closedBreaks[0].to);
  if (dayStr <= mostRecentTo) return false; // isOnBreak/freqForDay already mask through `to`
  const daysSinceResume = Math.round((new Date(dayStr) - new Date(mostRecentTo)) / 86400000);
  if (daysSinceResume > RESUME_GRACE_DAYS) return false;
  const shotLocalDate = iso => localDateStr(new Date(iso));
  const hasRedosedByDay = shots.some(s => s.compound === key && shotLocalDate(s.date) > mostRecentTo && shotLocalDate(s.date) <= dayStr);
  return !hasRedosedByDay;
}

// `shieldedDates` (an array of local YYYY-MM-DD strings, persisted per-user -- see
// firstUnshieldedMiss below) are days a shield already covered: a scheduled-but-unlogged
// day in this set counts toward the streak exactly like a logged day, instead of breaking
// it. Persisting the specific date (rather than just decrementing a counter) is what makes
// the protection survive a page reload -- streakSaves/currentStreak alone are in-memory
// React state, so without a durable record of *which day* was forgiven, a shield's effect
// would vanish the moment the app recomputes from scratch.
export function calcStreak(shots, compounds, today = new Date(), shieldedDates = []) {
  const activeCmpds = Object.entries(compounds).filter(([, c]) => c.freq?.length > 0 && !c.archived);
  if (!activeCmpds.length) return 0;
  let streak = 0;
  const shotLocalDate = iso => localDateStr(new Date(iso));
  for (let d = 0; d < 365; d++) {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() - d);
    const dayName = DAYS[day.getDay()];
    const dayStr = localDateStr(day);
    const scheduled = activeCmpds.filter(([k, c]) => freqForDay(c, dayStr).includes(dayName) && !resumeGraceActive(c, k, shots, dayStr));
    if (!scheduled.length) continue;
    const allLogged = scheduled.every(([k]) => shots.some(s => s.compound === k && shotLocalDate(s.date) === dayStr));
    if (allLogged || shieldedDates.includes(dayStr)) { streak++; }
    else if (d === 0) { continue; }
    else { break; }
  }
  return streak;
}

// Walks the same backward scan as calcStreak (already-shielded days skipped, same
// leniency for today), and returns `{ dayStr, daysAgo }` for the first day that's
// scheduled, unmet, and not already shielded -- i.e. exactly the day
// calcStreak(..., shieldedDates) would next break on (daysAgo is that day's `d`, so a
// caller can gate auto-shielding to only recent misses -- see SHIELD_MISS_WINDOW_DAYS).
// Returns null if there's no such day within a year (fully compliant, or nothing left to
// protect). Kept separate from calcStreak so calcStreak's return type -- a plain number,
// asserted directly by tests/e2e/calc-streak.test.js -- never changes shape.
export function firstUnshieldedMiss(shots, compounds, today = new Date(), shieldedDates = []) {
  const activeCmpds = Object.entries(compounds).filter(([, c]) => c.freq?.length > 0 && !c.archived);
  if (!activeCmpds.length) return null;
  const shotLocalDate = iso => localDateStr(new Date(iso));
  for (let d = 0; d < 365; d++) {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() - d);
    const dayName = DAYS[day.getDay()];
    const dayStr = localDateStr(day);
    if (shieldedDates.includes(dayStr)) continue;
    const scheduled = activeCmpds.filter(([k, c]) => freqForDay(c, dayStr).includes(dayName) && !resumeGraceActive(c, k, shots, dayStr));
    if (!scheduled.length) continue;
    const allLogged = scheduled.every(([k]) => shots.some(s => s.compound === k && shotLocalDate(s.date) === dayStr));
    if (allLogged || d === 0) continue;
    return { dayStr, daysAgo: d };
  }
  return null;
}

// Milestone cards and shield grants celebrate a streak that grew because a dose of that kind was
// just logged. Deleting a stack item or editing a schedule can also raise the computed streak
// (fewer scheduled days to miss) — that's a recalculation, not an achievement.
export const streakRoseFromLogging = ({ initialized, prevStreak, newStreak, prevCount, newCount }) =>
  !!initialized && prevCount != null && newStreak > prevStreak && newCount > prevCount;
